/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Locale, LocaleKeys } from '../../../core/locale';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { TScreen } from '../../../core/navigation/types';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import { setHelpOptionsList } from '../../../store/settings/help/help.reducers';
import { List, Mousetrap, ScreenPreview } from '../../common';
import { useScreenController } from '../../hooks';
import { SettingsTemplate } from '../SettingsTemplate';

import '../SettingsCommon.scss';

const TAG = 'HelpScreen';

const HelpScreen: FunctionComponent = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [isCursorActive, setisCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector((state: RootState) => state.help);

    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.value === selectedOption?.value ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.HELP_SCREEN,
            screenController: ScreenControllerTypes.HELP_SCREEN,
            taskController: ScreenTaskControllerTypes.NO_TASK,
        },
    });

    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.HELP, true));
    };

    useEffect(() => {
        dispatch(setHelpOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    const renderHelpList = () => {
        return (
            <div className="settings-common-content">
                <List
                    type={ListTypes.HELP}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.HELP}`}
                            className={`item ${itemClass}`}
                        >
                            <span>{item.value}</span>
                        </Mousetrap>
                    )}
                />
            </div>
        );
    };

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_HELP).toUpperCase()}>
            {renderHelpList()}
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default HelpScreen;
